/** @file treeUi: the node tree's own UI state per store (search, chips, view-hidden set, rename, anchor). */
import { createStore } from 'zustand/vanilla';
import type { StoreApi } from 'zustand/vanilla';
import type { EditorStore } from '../../state/editorTypes';

/**
 * Tree-only state, never in the document or in undo: `search` (the box text), `groups` (chips on), `viewHidden` (the
 * eye: viewport-only hide, read by the viewport and Art previews, never `node.hidden`), `renaming` (the row with an
 * open name field, with its inline `renameError`) and `anchor` (where a Shift range starts).
 */
export type TreeUiState = {
  search: string;
  groups: ReadonlySet<number>;
  viewHidden: ReadonlySet<string>;
  renaming: string | null;
  renameError: string | null;
  anchor: string | null;
  setSearch: (search: string) => void;
  toggleGroup: (group: number) => void;
  clearFilters: () => void;
  toggleViewHidden: (name: string) => void;
  hideInView: (names: readonly string[]) => void;
  showAllInView: () => void;
  startRename: (name: string) => void;
  stopRename: () => void;
  setRenameError: (message: string | null) => void;
  setAnchor: (name: string | null) => void;
  /** After a successful `node.rename`: carries the view-hidden mark and the anchor over to the new name. */
  renamed: (from: string, to: string) => void;
};

const registry = new WeakMap<EditorStore, StoreApi<TreeUiState>>();

function create(): StoreApi<TreeUiState> {
  return createStore<TreeUiState>()((set) => ({
    search: '', groups: new Set(), viewHidden: new Set(), renaming: null, renameError: null, anchor: null,
    setSearch: (search) => { set({ search }); },
    toggleGroup: (group) => {
      set((s) => {
        const groups = new Set(s.groups);
        if (!groups.delete(group)) groups.add(group);
        return { groups };
      });
    },
    clearFilters: () => { set({ search: '', groups: new Set() }); },
    toggleViewHidden: (name) => {
      set((s) => {
        const viewHidden = new Set(s.viewHidden);
        if (!viewHidden.delete(name)) viewHidden.add(name);
        return { viewHidden };
      });
    },
    hideInView: (names) => { set((s) => ({ viewHidden: new Set([...s.viewHidden, ...names]) })); },
    showAllInView: () => { set({ viewHidden: new Set() }); },
    startRename: (name) => { set({ renaming: name, renameError: null }); },
    stopRename: () => { set({ renaming: null, renameError: null }); },
    setRenameError: (renameError) => { set({ renameError }); },
    setAnchor: (anchor) => { set({ anchor }); },
    renamed: (from, to) => {
      set((s) => ({
        viewHidden: s.viewHidden.has(from) ? new Set([...s.viewHidden].map((n) => (n === from ? to : n))) : s.viewHidden,
        anchor: s.anchor === from ? to : s.anchor,
      }));
    },
  }));
}

/** The tree UI store of `store` (created on first use, so the viewport and the menu can read it without the panel). */
export function treeUi(store: EditorStore): StoreApi<TreeUiState> {
  let ui = registry.get(store);
  if (ui === undefined) {
    ui = create();
    registry.set(store, ui);
  }
  return ui;
}
